/**
 * Pure date/scheduling helpers for the staff Распоред (schedule) feature.
 * All date math is in LOCAL time (weeks start Monday); persistence converts to
 * ISO/UTC at the edge via toISOString(). Kept free of React so it unit-tests
 * cleanly.
 */
import type { TemplateEntry } from '@gym/api-client';

import type { Tone } from '../../../components/ui/StatusPill.js';
import type { ShiftRow } from '../hooks/useStaff.js';

export type ScheduleView = 'day' | 'week' | 'month';

/** Day view timeline window (hours). Shifts are drawn within [START, END). */
export const DAY_START_HOUR = 6;
export const DAY_END_HOUR = 24;

export function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

export function addMonths(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(1); // avoid month-length overflow (e.g. Jan 31 → Mar)
  x.setMonth(x.getMonth() + n);
  return x;
}

/** Monday-based start of the week containing `d`. */
export function startOfWeek(d: Date): Date {
  const x = startOfDay(d);
  const dow = (x.getDay() + 6) % 7; // Mon=0 … Sun=6
  x.setDate(x.getDate() - dow);
  return x;
}

export function startOfMonth(d: Date): Date {
  const x = startOfDay(d);
  x.setDate(1);
  return x;
}

export function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** The 7 days (Mon→Sun) of the week containing `anchor`. */
export function weekDays(anchor: Date): Date[] {
  const s = startOfWeek(anchor);
  return Array.from({ length: 7 }, (_, i) => addDays(s, i));
}

/** A 6×7 calendar grid (Monday-first) covering the month containing `anchor`. */
export function monthMatrix(anchor: Date): Date[][] {
  const gridStart = startOfWeek(startOfMonth(anchor));
  return Array.from({ length: 6 }, (_, w) =>
    Array.from({ length: 7 }, (_, i) => addDays(gridStart, w * 7 + i)),
  );
}

/** Inclusive ISO [from, to] window to query for a given view + anchor. */
export function rangeForView(view: ScheduleView, anchor: Date): { from: string; to: string } {
  let start: Date;
  let endExclusive: Date;
  if (view === 'day') {
    start = startOfDay(anchor);
    endExclusive = addDays(start, 1);
  } else if (view === 'week') {
    start = startOfWeek(anchor);
    endExclusive = addDays(start, 7);
  } else {
    start = startOfWeek(startOfMonth(anchor)); // visible grid start
    endExclusive = addDays(start, 42); // 6 weeks
  }
  // listShifts uses `lte` on scheduled_start; subtract 1ms so a boundary
  // midnight shift isn't double-counted across adjacent windows.
  return { from: start.toISOString(), to: new Date(endExclusive.getTime() - 1).toISOString() };
}

/** Local 'YYYY-MM-DD' for a date input. */
export function toDateInput(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Local 'HH:MM' for a time input. */
export function toTimeInput(iso: string | Date): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Combine a 'YYYY-MM-DD' + 'HH:MM' into a local Date. */
export function combineDateTime(dateStr: string, timeStr: string): Date {
  const [ys, ms, ds] = dateStr.split('-');
  const [hs, mins] = timeStr.split(':');
  return new Date(Number(ys), Number(ms) - 1, Number(ds), Number(hs), Number(mins), 0, 0);
}

/** Duration of a shift in hours (0 when missing/invalid). */
export function shiftHours(startIso: string | null, endIso: string | null): number {
  if (!startIso || !endIso) return 0;
  const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
  return ms > 0 ? ms / 3_600_000 : 0;
}

/** Compact hours label, e.g. 7.5 → "7.5", 8 → "8". */
export function formatHours(h: number): string {
  return String(Math.round(h * 10) / 10);
}

/** Decimal hour-of-day (e.g. 09:30 → 9.5) in local time. */
export function hourOfDay(iso: string): number {
  const d = new Date(iso);
  return d.getHours() + d.getMinutes() / 60;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/** Left/width percentages for a shift bar on the day-view hour axis. */
export function barGeometry(
  startIso: string,
  endIso: string,
): { leftPct: number; widthPct: number } {
  const span = DAY_END_HOUR - DAY_START_HOUR;
  const s = clamp(hourOfDay(startIso), DAY_START_HOUR, DAY_END_HOUR);
  let e = hourOfDay(endIso);
  if (e <= s) e = DAY_END_HOUR; // ends at/after midnight → clamp to axis end
  e = clamp(e, DAY_START_HOUR, DAY_END_HOUR);
  const left = ((s - DAY_START_HOUR) / span) * 100;
  const width = Math.max(3, ((e - s) / span) * 100);
  return { leftPct: clamp(left, 0, 100), widthPct: clamp(width, 0, 100 - left) };
}

/** Number of days in the month containing `d`. */
export function daysInMonth(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
}

/** The period's starting day for a view (day=that day, week=Monday, month=1st). */
export function periodStart(view: ScheduleView, anchor: Date): Date {
  if (view === 'day') return startOfDay(anchor);
  if (view === 'week') return startOfWeek(anchor);
  return startOfMonth(anchor);
}

/** The period's length in days (day=1, week=7, month=days-in-month). */
export function periodLength(view: ScheduleView, anchor: Date): number {
  if (view === 'day') return 1;
  if (view === 'week') return 7;
  return daysInMonth(anchor);
}

function minutesOfDay(iso: string): number {
  const d = new Date(iso);
  return d.getHours() * 60 + d.getMinutes();
}

/**
 * Capture the shifts within the current period as scope-relative template
 * entries (day_offset from the period start + start/end minutes-of-day). For the
 * month view this filters out the leading/trailing grid days of other months.
 */
export function buildTemplateEntries(
  view: ScheduleView,
  anchor: Date,
  shifts: ShiftRow[],
): TemplateEntry[] {
  const start = periodStart(view, anchor);
  const endExclusive = addDays(start, periodLength(view, anchor));
  const entries: TemplateEntry[] = [];
  for (const sh of shifts) {
    if (!sh.scheduled_start || !sh.scheduled_end) continue;
    const s = new Date(sh.scheduled_start);
    if (s < start || s >= endExclusive) continue;
    const dayOffset = Math.round((startOfDay(s).getTime() - start.getTime()) / 86_400_000);
    entries.push({
      staff_id: sh.staff_id,
      day_offset: dayOffset,
      start_minutes: minutesOfDay(sh.scheduled_start),
      end_minutes: minutesOfDay(sh.scheduled_end),
    });
  }
  return entries;
}

export interface ShiftInput {
  staffId: string;
  scheduledStart: string;
  scheduledEnd: string;
}

/**
 * Expand template entries into concrete shift inputs for the target period.
 * Skips entries for staff no longer present and (month scope) day_offsets that
 * fall outside a shorter target month.
 */
export function templateToShiftInputs(
  scope: ScheduleView,
  anchor: Date,
  entries: TemplateEntry[],
  validStaffIds: Set<string>,
): ShiftInput[] {
  const start = periodStart(scope, anchor);
  const len = periodLength(scope, anchor);
  const out: ShiftInput[] = [];
  for (const e of entries) {
    if (e.day_offset >= len || e.day_offset < 0) continue;
    if (!validStaffIds.has(e.staff_id)) continue;
    const day = addDays(start, e.day_offset);
    const ss = new Date(day);
    ss.setHours(Math.floor(e.start_minutes / 60), e.start_minutes % 60, 0, 0);
    const ee = new Date(day);
    ee.setHours(Math.floor(e.end_minutes / 60), e.end_minutes % 60, 0, 0);
    out.push({
      staffId: e.staff_id,
      scheduledStart: ss.toISOString(),
      scheduledEnd: ee.toISOString(),
    });
  }
  return out;
}

/** Role → semantic tone, so each employee's shifts read consistently. */
export function roleTone(role: string | null | undefined): Tone {
  switch (role) {
    case 'owner':
      return 'success';
    case 'manager':
      return 'warning';
    case 'trainer':
      return 'info';
    case 'front_desk':
      return 'neutral';
    default:
      return 'muted';
  }
}
